import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getArticle, getAds } from "../lib/api";
import { ArticleCard, SectionHeading, fmtDate } from "../components/ArticleCard";
import { AdSlot, NativeAd } from "../components/Ads";
import { Clock, Share2, Bookmark } from "lucide-react";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1595432849291-3036ea5e5475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const onImgError = (e) => {
  if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG;
};

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [ads, setAds] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setNotFound(false);
    getArticle(slug).then(setData).catch(() => setNotFound(true));
    getAds().then(setAds).catch(() => {});
  }, [slug]);

  if (notFound)
    return (
      <div className="paper-texture flex min-h-[60vh] flex-col items-center justify-center px-4 text-center" data-testid="article-not-found">
        <p className="font-display text-6xl font-extrabold text-[var(--navy)]">404</p>
        <h1 className="font-display mt-2 text-2xl font-bold text-[var(--ink)]">Story not found</h1>
        <p className="font-serif-body mt-2 text-slate-600">This article may have been moved or removed from the archive.</p>
        <Link to="/" className="font-ui mt-6 rounded-full bg-[var(--gold)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
          Back to Home
        </Link>
      </div>
    );

  if (!data) return <div className="mx-auto max-w-3xl px-4 py-20 text-center font-ui text-slate-500">Loading article…</div>;

  const { article, related } = data;
  const sidebarAd = ads.find((a) => a.slot === "sidebar");
  const nativeAd = ads.find((a) => a.slot === "native");
  const paras = article.body || [];

  return (
    <div className="paper-texture min-h-screen">
      <article className="mx-auto max-w-3xl px-4 py-10">
        <div className="flex items-center gap-2">
          <Link to={`/section/${article.category}`} className="eyebrow text-[var(--gold)] hover:underline">
            {article.category === "sports" ? "High School Sports" : article.category === "news" ? "Local News" : article.category}
          </Link>
          {article.breaking && <span className="eyebrow rounded-sm bg-[var(--breaking)] px-1.5 py-0.5 text-[9px] text-white">Breaking</span>}
        </div>

        <h1 className="font-display mt-3 text-4xl font-extrabold leading-tight tracking-tight text-[var(--ink)] sm:text-5xl">
          {article.title}
        </h1>
        <p className="font-serif-body mt-4 text-xl leading-relaxed text-slate-600">{article.excerpt}</p>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-y border-[var(--paper-border)] py-3">
          <div className="flex items-center gap-3">
            {article.author_avatar && <img src={article.author_avatar} alt={article.author_name} onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-11 w-11 rounded-full object-cover" />}
            <div>
              <p className="font-ui text-sm font-bold text-[var(--ink)]">By {article.author_name}</p>
              <p className="font-ui text-xs text-slate-500">
                {article.author_role} · {fmtDate(article.published_at)} · <Clock size={11} className="inline" /> {article.read_time} min read
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--paper-border)] text-slate-500 hover:text-[var(--navy)]" data-testid="share-btn"><Share2 size={16} /></button>
            <button className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--paper-border)] text-slate-500 hover:text-[var(--navy)]" data-testid="bookmark-btn"><Bookmark size={16} /></button>
          </div>
        </div>

        <div className="img-zoom mt-6 overflow-hidden rounded-lg">
          <img src={article.image || FALLBACK_IMG} alt={article.title} onError={onImgError} className="h-80 w-full object-cover sm:h-[28rem]" />
        </div>

        <div className="relative mt-8">
          <div>
            {paras.map((p, i) => (
              <p key={i} className="font-serif-body mb-5 text-lg leading-[1.85] text-slate-800">{p}</p>
            ))}
          </div>

          {nativeAd && paras.length > 2 && (
            <div className="my-8"><NativeAd ad={nativeAd} /></div>
          )}
        </div>

        {sidebarAd && (
          <div className="mt-10"><AdSlot ad={sidebarAd} className="mx-auto max-w-md rounded-lg" /></div>
        )}
      </article>

      {related?.length > 0 && (
        <div className="mx-auto max-w-5xl px-4 pb-10">
          <SectionHeading title="More from this section" to={`/section/${article.category}`} />
          <div className="grid gap-8 sm:grid-cols-3">
            {related.map((a) => <ArticleCard key={a.slug} article={a} />)}
          </div>
        </div>
      )}
    </div>
  );
}
